'use client';

import { HeartIcon } from '@phosphor-icons/react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { getFavoritesAriaLabel, ICON_SIZE } from '../../lib/header-constants';
import { cn } from '@/shared/utils/cn';

const PULSE_HOLD_MS = 900;

interface HeaderFavoritesLinkProps {
  count: number;
  href: string;
  pulseKey?: number;
}

const HeaderFavoritesLink = ({ count, href, pulseKey }: HeaderFavoritesLinkProps) => {
  const [isFilled, setIsFilled] = useState(false);
  const lastPulseKeyRef = useRef(pulseKey);

  useEffect(() => {
    if (pulseKey === lastPulseKeyRef.current) return undefined;
    lastPulseKeyRef.current = pulseKey;
    setIsFilled(true);

    const timer = window.setTimeout(() => setIsFilled(false), PULSE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [pulseKey]);

  return (
    <Link
      aria-label={getFavoritesAriaLabel(count)}
      className={cn(
        'rounded-adipa-full text-adipa-primary relative hidden h-11 w-11 items-center justify-center lg:inline-flex',
        'duration-adipa-fast hover:bg-adipa-surface-lavender cursor-pointer transition ease-out motion-reduce:transition-none',
        'active:scale-95',
        'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
      )}
      data-state={isFilled ? 'filled' : 'idle'}
      href={href}
    >
      <span className="relative inline-flex">
        <HeartIcon size={ICON_SIZE} weight="bold" />
        <HeartIcon
          aria-hidden="true"
          className={cn(
            'absolute inset-0 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
            isFilled ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
          )}
          size={ICON_SIZE}
          weight="fill"
        />
      </span>

      {count > 0 ? (
        <span
          aria-hidden="true"
          className="rounded-adipa-full bg-adipa-primary global-header-cart-badge-text text-adipa-primary-foreground absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center font-bold tabular-nums"
        >
          {count}
        </span>
      ) : null}
    </Link>
  );
};

export default HeaderFavoritesLink;
